import React, { useCallback, useState } from 'react';
import {
  Heading,
  Text,
} from '@vibe/core';
import {
  Board,
  Security,
  Calendar,
  Bolt,
  Time,
  Event,
  Team,
  Tags,
} from '@vibe/icons';
import PermissionManagementPanel from './PermissionManagementPanel';
import LeaveSettingsPanel from './LeaveSettingsPanel';
import WorkloadDefaultsPanel from './WorkloadDefaultsPanel';
import BoardsConfigurationPanel from './BoardsConfigurationPanel';
import BoardSettings from './BoardSettings';
import CapacitySchemeList from '../capacity/CapacitySchemeList';
import HolidayCalendarList from '../holiday/HolidayCalendarList';
import CapacitySchemeEditor from '../capacity/CapacitySchemeEditor';
import HolidayCalendarEditor from '../holiday/HolidayCalendarEditor';
import TimesheetPermissionsPanel from './TimesheetPermissionsPanel';
import TimesheetConfigPanel from './TimesheetConfigPanel';
import WorklogCategoriesPanel from './WorklogCategoriesPanel';
import type { CapacityScheme } from '../../api/capacity.api';
import type { HolidayCalendar } from '../../api/holiday.api';

// ─── Tab definitions ──────────────────────────────────────────────────────────

type AppSettingsTab =
  | 'boards'
  | 'permissions'
  | 'leave'
  | 'timesheet-access'
  | 'timesheet-config'
  | 'workload'
  | 'capacity'
  | 'holidays'
  | 'worklog-categories';

interface TabDef {
  key: AppSettingsTab;
  label: string;
  description: string;
  icon: React.FC<Record<string, unknown>>;
  section?: string;
}

const TABS: TabDef[] = [
  { key: 'boards', label: 'Boards', description: 'Field mappings & board settings', icon: Board, section: 'App Settings' },
  { key: 'permissions', label: 'Permissions', description: 'Roles & access control', icon: Security, section: 'App Settings' },
  { key: 'leave', label: 'Leave Settings', description: 'Leave types & approver assignments', icon: Calendar, section: 'App Settings' },
  { key: 'timesheet-access', label: 'Timesheet Access', description: 'Timesheet viewers & approvers', icon: Team, section: 'App Settings' },
  { key: 'timesheet-config', label: 'Timesheet Config', description: 'Approval workflow & period settings', icon: Time, section: 'App Settings' },
  { key: 'workload', label: 'Workload', description: 'Default workload settings', icon: Bolt, section: 'App Settings' },
  { key: 'capacity', label: 'Capacity', description: 'Work capacity schemes', icon: Time, section: 'App Settings' },
  { key: 'holidays', label: 'Holidays', description: 'Holiday calendar schemes', icon: Event, section: 'App Settings' },
  { key: 'worklog-categories', label: 'Worklog Categories', description: 'Billable & non-billable time classifications', icon: Tags, section: 'App Settings' },
];

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Main app settings panel with tab navigation.
 */
const AppSettings: React.FC = () => {
  const [activeTab, setActiveTab] = useState<AppSettingsTab>('boards');

  // Board configuration editor state
  const [editingBoardId, setEditingBoardId] = useState<number | null>(null);

  const handleEditBoard = useCallback((boardId: number) => {
    setEditingBoardId(boardId);
  }, []);

  const handleBoardEditClose = useCallback(() => {
    setEditingBoardId(null);
  }, []);

  // Capacity scheme editor state
  const [editingCapacityScheme, setEditingCapacityScheme] =
    useState<CapacityScheme | null>(null);
  const [isCreatingCapacity, setIsCreatingCapacity] = useState(false);

  // Holiday calendar editor state
  const [editingHolidayCalendar, setEditingHolidayCalendar] =
    useState<HolidayCalendar | null>(null);
  const [isCreatingHoliday, setIsCreatingHoliday] = useState(false);

  // ─── Capacity scheme handlers ─────────────────────────────────────────────

  const handleEditCapacityScheme = useCallback((scheme: CapacityScheme) => {
    setEditingCapacityScheme(scheme);
    setIsCreatingCapacity(false);
  }, []);

  const handleCreateCapacityScheme = useCallback(() => {
    setEditingCapacityScheme(null);
    setIsCreatingCapacity(true);
  }, []);

  const handleCapacitySaveOrCancel = useCallback(() => {
    setEditingCapacityScheme(null);
    setIsCreatingCapacity(false);
  }, []);

  // ─── Holiday calendar handlers ────────────────────────────────────────────

  const handleEditHolidayCalendar = useCallback((cal: HolidayCalendar) => {
    setEditingHolidayCalendar(cal);
    setIsCreatingHoliday(false);
  }, []);

  const handleCreateHolidayCalendar = useCallback(() => {
    setEditingHolidayCalendar(null);
    setIsCreatingHoliday(true);
  }, []);

  const handleHolidaySaveOrCancel = useCallback(() => {
    setEditingHolidayCalendar(null);
    setIsCreatingHoliday(false);
  }, []);

  // ─── Render ───────────────────────────────────────────────────────────────

  const activeTabDef = TABS.find((t) => t.key === activeTab) ?? TABS[0];

  // Group tabs by section for rendering section headers
  const renderNav = () => {
    let lastSection = '';
    return TABS.map((tab) => {
      const Icon = tab.icon;
      const isActive = activeTab === tab.key;
      const showSection = tab.section && tab.section !== lastSection;
      lastSection = tab.section ?? '';
      return (
        <React.Fragment key={tab.key}>
          {showSection && (
            <div className="rp-app-settings__nav-section">
              {tab.section}
            </div>
          )}
          <button
            className={`rp-app-settings__nav-item ${isActive ? 'rp-app-settings__nav-item--active' : ''}`}
            onClick={() => setActiveTab(tab.key)}
          >
            <Icon />
            <span className="rp-app-settings__nav-item-label">{tab.label}</span>
          </button>
        </React.Fragment>
      );
    });
  };

  return (
    <div className="rp-app-settings">
      {/* Vertical section nav */}
      <nav className="rp-app-settings__nav">
        <div className="rp-app-settings__nav-header">
          <Heading type={Heading.types.H3}>Settings</Heading>
        </div>
        {renderNav()}
      </nav>

      {/* Content area */}
      <div className="rp-app-settings__body">
        <div className="rp-app-settings__body-header">
          <Heading type={Heading.types.H2}>{activeTabDef.label}</Heading>
          <Text type={Text.types.TEXT2} style={{ color: 'var(--secondary-text-color, #676879)', marginTop: 4 }}>
            {activeTabDef.description}
          </Text>
        </div>

        <div className="rp-app-settings__panel">
          {activeTab === 'boards' && (
            <div className="rp-app-settings__panel--boards">
              {editingBoardId !== null ? (
                <div className="rp-app-settings__board-editor">
                  <BoardSettings
                    boardId={editingBoardId}
                    onClose={handleBoardEditClose}
                  />
                </div>
              ) : (
                <BoardsConfigurationPanel onEditBoard={handleEditBoard} />
              )}
            </div>
          )}
          {activeTab === 'permissions' && <PermissionManagementPanel />}
          {activeTab === 'leave' && <LeaveSettingsPanel />}
          {activeTab === 'timesheet-access' && <TimesheetPermissionsPanel />}
          {activeTab === 'timesheet-config' && <TimesheetConfigPanel />}
          {activeTab === 'workload' && <WorkloadDefaultsPanel />}
          {activeTab === 'capacity' && (
            editingCapacityScheme || isCreatingCapacity ? (
              <CapacitySchemeEditor
                scheme={editingCapacityScheme}
                onSave={handleCapacitySaveOrCancel}
                onCancel={handleCapacitySaveOrCancel}
              />
            ) : (
              <CapacitySchemeList
                onEdit={handleEditCapacityScheme}
                onCreateNew={handleCreateCapacityScheme}
              />
            )
          )}
          {activeTab === 'worklog-categories' && <WorklogCategoriesPanel />}
          {activeTab === 'holidays' && (
            editingHolidayCalendar || isCreatingHoliday ? (
              <HolidayCalendarEditor
                calendar={editingHolidayCalendar}
                onSave={handleHolidaySaveOrCancel}
                onCancel={handleHolidaySaveOrCancel}
              />
            ) : (
              <HolidayCalendarList
                onEdit={handleEditHolidayCalendar}
                onCreateNew={handleCreateHolidayCalendar}
              />
            )
          )}
        </div>
      </div>
    </div>
  );
};

export default AppSettings;
